<template>
  <component
    :is="inline ? 'span' : 'div'"
    class="visually-hidden"
    :class="{ focusable }"
  >
    <slot />
  </component>
</template>

<script setup lang="ts">
/**
 * Accessibility component to hide content visually while keeping it available to screen readers
 *
 * Hides content from sighted users using CSS positioning and clipping,
 * but keeps it accessible to assistive technologies like screen readers.
 * Useful for providing additional context, skip links, or icon labels.
 *
 * @example Skip navigation link
 * ```vue
 * <VisuallyHidden focusable>
 *   <a href="#main-content">Skip to main content</a>
 * </VisuallyHidden>
 * ```
 *
 * @example Icon button label
 * ```vue
 * <BaseButton icon="search">
 *   <VisuallyHidden>Search</VisuallyHidden>
 * </BaseButton>
 * ```
 *
 * @example Additional form context
 * ```vue
 * <BaseLabel for="phone">Phone Number</BaseLabel>
 * <VisuallyHidden>
 *   Format: (555) 123-4567
 * </VisuallyHidden>
 * <BaseInput id="phone" type="tel" />
 * ```
 *
 * @example Table header context
 * ```vue
 * <th>
 *   <VisuallyHidden>Sort by </VisuallyHidden>
 *   Name
 * </th>
 * ```
 */
export interface VisuallyHiddenProps {
  /**
   * Render as inline span element (default) or block div element
   * @default true
   */
  inline?: boolean;

  /**
   * Allow element to become visible when focused (for skip links)
   * When true, focused elements become visible with styling
   * @default false
   */
  focusable?: boolean;
}

withDefaults(defineProps<VisuallyHiddenProps>(), {
  inline: true,
  focusable: false,
});
</script>

<style scoped>
.visually-hidden {
  /* Visually hide but keep accessible to screen readers */
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  clip-path: inset(50%);
  white-space: nowrap;
  border-width: 0;
}

/* Allow focusable elements (like skip links) to become visible on focus */
.visually-hidden.focusable:focus,
.visually-hidden.focusable:focus-within {
  position: static;
  width: auto;
  height: auto;
  padding: var(--gap-sm);
  margin: 0;
  overflow: visible;
  clip: auto;
  clip-path: none;
  white-space: normal;
  background: var(--color-primary-300);
  color: var(--color-light);
  z-index: 9999;
}
</style>
